import {compact} from '@benchling/js-utils';

import BenchlingConfig from 'BenchlingConfig';
import {ProteinAlignmentKind} from 'constants/AlignmentConstants';
import Type from 'molbio/maps/Type';
import {AminoAcidLayout} from 'molbio/proteins/AminoAcids';
import {areAaPartFeaturesEnabledForAntibody} from 'molbio/proteins/util/aminoAcidPartsUtil';
import {ProteinColorScheme} from 'molbio/proteins/util/ProteinColoringConstants';
import {PickKey} from 'util/Types';

// All layers that can be rendered in any of our SliceViews
export enum SliceComponentName {
  forwardPamSites = 'forwardPamSites',
  cutSites = 'cutSites',
  reversePamSites = 'reversePamSites',
  dna = 'dna',
  title = 'title',
  emptyPlaceholder = 'emptyPlaceholder',
  aminoAcids = 'aminoAcids',
  antibodyCDRAnnotations = 'antibodyCDRAnnotations',
  antibodyAlignmentCDRAnnotations = 'antibodyAlignmentCDRAnnotations',
  nucleotidesSearch = 'nucleotidesSearch',
  translations = 'translations',
  readingFrames = 'readingFrames',
  annotations = 'annotations',
  primers = 'primers',
  forwardPrimers = 'forwardPrimers',
  reversePrimers = 'reversePrimers',
  orfs = 'orfs',
  sequenceAxis = 'sequenceAxis',
  homologyRegionAxis = 'homologyRegionAxis',
  trace = 'trace',
  votes = 'votes',
  line = 'line',
  sequenceBiointelligentLinkHighlights = 'sequenceBiointelligentLinkHighlights',
  reverseSequenceBiointelligentLinkHighlightsSpace = 'reverseSequenceBiointelligentLinkHighlightsSpace',
  partAnnotations = 'partAnnotations',
  transcriptionAnnotations = 'transcriptionAnnotations',
  proteinPartAnnotations = 'proteinPartAnnotations',
  gapToRealAxis = 'gapToRealAxis',
  alignmentComments = 'alignmentComments',
  primerAssemblyMismatches = 'primerAssemblyMismatches',
  goldenGateCutSites = 'goldenGateCutSites',
  proteinAxis = 'proteinAxis',
  proteinAlignmentAxis = 'proteinAlignmentAxis',
  aminoAcidsSearch = 'aminoAcidsSearch',
  codonOptimization = 'codonOptimization',
  proteinAnnotations = 'proteinAnnotations',
  proteinAlignmentAnnotations = 'proteinAlignmentAnnotations',
  proteinParts = 'proteinParts',
  proteinPIGraph = 'proteinPIGraph',
  proteinHydrophobicityGraph = 'proteinHydrophobicityGraph',
  proteinAlignmentIdentityGraph = 'proteinAlignmentIdentityGraph',
  antibodyNumbering = 'antibodyNumbering',
  proteinLiabilitySites = 'proteinLiabilitySites',
  modifiedNucleotides = 'modifiedNucleotides',
  modifiedNucleotideSummary = 'modifiedNucleotideSummary',
  nucleotidesAxisOverlay = 'nucleotidesAxisOverlay',
  nucleotidesPositionHighlight = 'nucleotidesPositionHighlight',
  selectionHiddenWrapper = 'selectionHiddenWrapper',
  forwardTranscriptionLine = 'forwardTranscriptionLine',
  reverseTranscriptionLine = 'reverseTranscriptionLine',
  transcriptionArrow = 'transcriptionArrow',
}

// Inline layers is currently only implemented by Annotations.
export const LINEAR_LAYER_INLINE = 'inlineLayer'; // For showing annotation names regardless of size
export const LINEAR_LAYER_INLINE_PDF = 'inlineLayer-pdf';
export const LINEAR_INLINE_LAYERS = [LINEAR_LAYER_INLINE, LINEAR_LAYER_INLINE_PDF];
export const LINEAR_MAP_Y_COMPONENTS: Array<SliceComponentName> = compact([
  SliceComponentName.emptyPlaceholder,
  SliceComponentName.cutSites,
  // Keep transcriptionArrow.secondarySettings in maps/linear/SliceView.tsx consistent with any changes to the
  // toggleable views between transcriptionArrow and reverseTranscriptionLine.
  // TranscriptionArrow draws a vertical line to each transcription line. The array in secondarySettings
  // ensures the height of this line is refreshed when inbetween slices are toggled on/off
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? null : SliceComponentName.transcriptionArrow,
  SliceComponentName.forwardPamSites,
  SliceComponentName.forwardPrimers,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? null : SliceComponentName.forwardTranscriptionLine,
  SliceComponentName.sequenceBiointelligentLinkHighlights,
  SliceComponentName.primerAssemblyMismatches,
  SliceComponentName.goldenGateCutSites,
  SliceComponentName.codonOptimization,
  SliceComponentName.nucleotidesSearch,
  SliceComponentName.dna,
  SliceComponentName.selectionHiddenWrapper,
  SliceComponentName.reverseSequenceBiointelligentLinkHighlightsSpace,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? SliceComponentName.partAnnotations : null,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? SliceComponentName.transcriptionAnnotations : null,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? null : SliceComponentName.reverseTranscriptionLine,
  SliceComponentName.reversePrimers,
  SliceComponentName.reversePamSites,
  SliceComponentName.translations,
  SliceComponentName.orfs,
  SliceComponentName.annotations,
  SliceComponentName.sequenceAxis,
  SliceComponentName.homologyRegionAxis,
]);
export const RNA_LINEAR_MAP_Y_COMPONENTS: Array<SliceComponentName> = compact([
  SliceComponentName.emptyPlaceholder,
  SliceComponentName.cutSites,
  SliceComponentName.forwardPrimers,
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.modifiedNucleotideSummary : null,
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.nucleotidesPositionHighlight : null,
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.modifiedNucleotides : null,
  SliceComponentName.sequenceBiointelligentLinkHighlights,
  SliceComponentName.reverseSequenceBiointelligentLinkHighlightsSpace,
  SliceComponentName.nucleotidesSearch,
  SliceComponentName.dna,
  SliceComponentName.selectionHiddenWrapper,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? SliceComponentName.partAnnotations : null,
  SliceComponentName.reversePrimers,
  SliceComponentName.translations,
  SliceComponentName.orfs,
  SliceComponentName.annotations,
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.nucleotidesAxisOverlay : null,
  SliceComponentName.sequenceAxis,
]);

export const OLIGO_MAP_Y_COMPONENTS: Array<SliceComponentName> = compact([
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.nucleotidesPositionHighlight : null,
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.modifiedNucleotides : null,
  SliceComponentName.nucleotidesSearch,
  SliceComponentName.dna,
  SliceComponentName.selectionHiddenWrapper,
  SliceComponentName.annotations,
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.nucleotidesAxisOverlay : null,
  SliceComponentName.sequenceAxis,
]);
export const OLIGO_MAP_Z_COMPONENTS: Array<SliceComponentName> = compact([
  SliceComponentName.nucleotidesSearch,
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.nucleotidesPositionHighlight : null,
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.modifiedNucleotides : null,
  SliceComponentName.dna,
  SliceComponentName.selectionHiddenWrapper,
  SliceComponentName.annotations,
  SliceComponentName.sequenceAxis,
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.nucleotidesAxisOverlay : null,
]);

export const LINEAR_MAP_Z_COMPONENTS: Array<SliceComponentName> = compact([
  SliceComponentName.emptyPlaceholder,
  SliceComponentName.sequenceBiointelligentLinkHighlights,
  SliceComponentName.reverseSequenceBiointelligentLinkHighlightsSpace,
  SliceComponentName.dna,
  SliceComponentName.selectionHiddenWrapper,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? SliceComponentName.partAnnotations : null,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? SliceComponentName.transcriptionAnnotations : null,
  SliceComponentName.cutSites,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? null : SliceComponentName.transcriptionArrow,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? null : SliceComponentName.forwardTranscriptionLine,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? null : SliceComponentName.reverseTranscriptionLine,
  SliceComponentName.forwardPamSites,
  SliceComponentName.reversePamSites,
  SliceComponentName.codonOptimization,
  SliceComponentName.nucleotidesSearch,
  SliceComponentName.primerAssemblyMismatches,
  SliceComponentName.goldenGateCutSites,
  SliceComponentName.translations,
  SliceComponentName.orfs,
  SliceComponentName.annotations,
  SliceComponentName.forwardPrimers,
  SliceComponentName.reversePrimers,
  SliceComponentName.sequenceAxis,
  SliceComponentName.homologyRegionAxis,
]);
export const RNA_LINEAR_MAP_Z_COMPONENTS: Array<SliceComponentName> = compact([
  SliceComponentName.emptyPlaceholder,
  SliceComponentName.sequenceBiointelligentLinkHighlights,
  SliceComponentName.reverseSequenceBiointelligentLinkHighlightsSpace,
  SliceComponentName.nucleotidesSearch,
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.modifiedNucleotideSummary : null,
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.nucleotidesPositionHighlight : null,
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.modifiedNucleotides : null,
  SliceComponentName.dna,
  SliceComponentName.selectionHiddenWrapper,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? SliceComponentName.partAnnotations : null,
  SliceComponentName.cutSites,
  SliceComponentName.translations,
  SliceComponentName.orfs,
  SliceComponentName.annotations,
  SliceComponentName.forwardPrimers,
  SliceComponentName.reversePrimers,
  SliceComponentName.sequenceAxis,
  BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES ? SliceComponentName.nucleotidesAxisOverlay : null,
]);

export const PROTEIN_MAP_Y_COMPONENTS: Array<SliceComponentName> = compact([
  SliceComponentName.proteinPIGraph,
  SliceComponentName.proteinHydrophobicityGraph,
  BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS ? SliceComponentName.antibodyCDRAnnotations : null,
  BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS ? SliceComponentName.antibodyNumbering : null,
  BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS ? SliceComponentName.proteinLiabilitySites : null,
  areAaPartFeaturesEnabledForAntibody() ? SliceComponentName.proteinParts : null,
  SliceComponentName.aminoAcidsSearch,
  SliceComponentName.aminoAcids,
  SliceComponentName.selectionHiddenWrapper,
  areAaPartFeaturesEnabledForAntibody() ? SliceComponentName.proteinPartAnnotations : null,
  SliceComponentName.proteinAnnotations,
  SliceComponentName.proteinAxis,
]);

export const PROTEIN_MAP_Z_COMPONENTS: Array<SliceComponentName> = compact([
  areAaPartFeaturesEnabledForAntibody() ? SliceComponentName.proteinParts : null,
  SliceComponentName.proteinPIGraph,
  SliceComponentName.proteinHydrophobicityGraph,
  BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS ? SliceComponentName.antibodyCDRAnnotations : null,
  BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS ? SliceComponentName.antibodyNumbering : null,
  BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS ? SliceComponentName.proteinLiabilitySites : null,
  SliceComponentName.aminoAcids,
  SliceComponentName.selectionHiddenWrapper,
  areAaPartFeaturesEnabledForAntibody() ? SliceComponentName.proteinPartAnnotations : null,
  SliceComponentName.aminoAcidsSearch,
  SliceComponentName.proteinAnnotations,
  SliceComponentName.proteinAxis,
]);

export const MINI_MAP_Y_COMPONENTS: Array<SliceComponentName> = compact([
  SliceComponentName.title,
  SliceComponentName.cutSites,
  SliceComponentName.sequenceBiointelligentLinkHighlights,
  SliceComponentName.dna,
  SliceComponentName.selectionHiddenWrapper,
  SliceComponentName.sequenceAxis,
  SliceComponentName.homologyRegionAxis,
  SliceComponentName.reverseSequenceBiointelligentLinkHighlightsSpace,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? SliceComponentName.partAnnotations : null,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? SliceComponentName.transcriptionAnnotations : null,
  SliceComponentName.orfs,
  SliceComponentName.translations,
  SliceComponentName.annotations,
  SliceComponentName.primers,
]);
export const MINI_MAP_Z_COMPONENTS: Array<SliceComponentName> = compact([
  SliceComponentName.title,
  SliceComponentName.sequenceBiointelligentLinkHighlights,
  SliceComponentName.reverseSequenceBiointelligentLinkHighlightsSpace,
  SliceComponentName.cutSites,
  SliceComponentName.dna,
  SliceComponentName.selectionHiddenWrapper,
  SliceComponentName.sequenceAxis,
  SliceComponentName.homologyRegionAxis,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? SliceComponentName.partAnnotations : null,
  BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS ? SliceComponentName.transcriptionAnnotations : null,
  SliceComponentName.orfs,
  SliceComponentName.translations,
  SliceComponentName.annotations,
  SliceComponentName.primers,
]);

export const NUCLEOTIDE_ALIGNMENT_ROW_Y_COMPONENTS: Array<SliceComponentName> = [
  SliceComponentName.votes,
  SliceComponentName.alignmentComments,
  SliceComponentName.trace,
  SliceComponentName.line,
  SliceComponentName.nucleotidesSearch,
  SliceComponentName.dna,
  SliceComponentName.selectionHiddenWrapper,
  SliceComponentName.sequenceAxis,
  SliceComponentName.gapToRealAxis,
  SliceComponentName.translations,
  SliceComponentName.readingFrames,
  SliceComponentName.annotations,
  SliceComponentName.primers,
];
export const NUCLEOTIDE_ALIGNMENT_ROW_Z_COMPONENTS: Array<SliceComponentName> = [
  SliceComponentName.votes,
  SliceComponentName.trace,
  SliceComponentName.line,
  SliceComponentName.dna,
  SliceComponentName.selectionHiddenWrapper,
  SliceComponentName.nucleotidesSearch,
  SliceComponentName.sequenceAxis,
  SliceComponentName.gapToRealAxis,
  SliceComponentName.translations,
  SliceComponentName.readingFrames,
  SliceComponentName.annotations,
  SliceComponentName.primers,
  SliceComponentName.alignmentComments,
];

export const PROTEIN_ALIGNMENT_ROW_Y_COMPONENTS: Array<SliceComponentName> = compact([
  SliceComponentName.proteinAlignmentIdentityGraph,
  SliceComponentName.proteinPIGraph,
  SliceComponentName.proteinHydrophobicityGraph,
  BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS
    ? SliceComponentName.antibodyAlignmentCDRAnnotations
    : null,
  BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS ? SliceComponentName.antibodyNumbering : null,
  BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS ? SliceComponentName.proteinLiabilitySites : null,
  SliceComponentName.line,
  SliceComponentName.aminoAcids,
  SliceComponentName.selectionHiddenWrapper,
  SliceComponentName.proteinAlignmentAnnotations,
  SliceComponentName.proteinAlignmentAxis,
]);
export const PROTEIN_ALIGNMENT_ROW_Z_COMPONENTS: Array<SliceComponentName> = compact([
  SliceComponentName.proteinAlignmentIdentityGraph,
  SliceComponentName.proteinPIGraph,
  SliceComponentName.proteinHydrophobicityGraph,
  BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS
    ? SliceComponentName.antibodyAlignmentCDRAnnotations
    : null,
  BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS ? SliceComponentName.antibodyNumbering : null,
  BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS ? SliceComponentName.proteinLiabilitySites : null,
  SliceComponentName.line,
  SliceComponentName.aminoAcids,
  SliceComponentName.selectionHiddenWrapper,
  SliceComponentName.proteinAlignmentAnnotations,
  SliceComponentName.proteinAlignmentAxis,
]);

export interface PolymerLayout {
  height?: number;
  svgHeight?: number;
  visible?: boolean;
  spacer?: number;
  // Vertical margin between this layout and other layouts
  vMargin: number;
  type?: {
    fontSize: number;
    letterSpacing?: number;
  };
  foregroundColoring?: string;
  showColoring?: boolean;
  // Vertical margin between layered elements within this layout
  vMarginSingle?: number;
}

export type ProteinLayouts = PickKey<
  typeof SliceComponentName,
  | 'aminoAcids'
  | 'proteinAlignmentIdentityGraph'
  | 'proteinHydrophobicityGraph'
  | 'proteinParts'
  | 'proteinPartAnnotations'
  | 'proteinPIGraph'
  | 'antibodyNumbering'
  | 'proteinLiabilitySites'
>;

type ModifiedSequenceLayouts = PickKey<
  typeof SliceComponentName,
  | 'modifiedNucleotides'
  | 'nucleotidesAxisOverlay'
  | 'nucleotidesPositionHighlight'
  | 'modifiedNucleotideSummary'
  | 'sequenceBiointelligentLinkHighlights'
  | 'reverseSequenceBiointelligentLinkHighlightsSpace'
  | 'partAnnotations'
  | 'primers'
  | 'nucleotidesSearch'
  | 'translations'
>;

type ModifiedOligoLayouts = PickKey<
  typeof SliceComponentName,
  'modifiedNucleotides' | 'nucleotidesAxisOverlay' | 'nucleotidesPositionHighlight' | 'nucleotidesSearch'
>;

type SequenceLayouts = PickKey<
  typeof SliceComponentName,
  | 'reverseTranscriptionLine'
  | 'forwardTranscriptionLine'
  | 'transcriptionArrow'
  | 'sequenceBiointelligentLinkHighlights'
  | 'reverseSequenceBiointelligentLinkHighlightsSpace'
  | 'partAnnotations'
  | 'transcriptionAnnotations'
  | 'primers'
  | 'nucleotidesSearch'
  | 'codonOptimization'
  | 'translations'
>;

type AlignmentLayouts = PickKey<
  typeof SliceComponentName,
  'primers' | 'votes' | 'nucleotidesSearch' | 'translations'
>;

export const AXIS_VMARGIN = 10;
export const ASSEMBLY_CONSTRUCT_AXIS_ADDITIONAL_HEIGHT = 6;

export function getDefaultAminoAcidsLayout(): AminoAcidLayout {
  return {
    visible: true,
    spacer: 4,
    vMargin: 0,
    type: {
      fontSize: 18,
      letterSpacing: 0.6,
    },
    foregroundColoring: ProteinColorScheme.RASMOL,
    showColoring: true,
  };
}

export interface BasicProteinLayout {
  height: number;
  vMargin: number;
}

export interface AntibodyNumberingLayout {
  vMargin: number;
  type: {fontSize: number};
}

export interface ProteinLayout {
  aminoAcids: AminoAcidLayout;
  antibodyNumbering: AntibodyNumberingLayout;
  proteinAlignmentIdentityGraph: BasicProteinLayout;
  proteinHydrophobicityGraph: BasicProteinLayout;
  proteinLiabilitySites: BasicProteinLayout;
  proteinParts: BasicProteinLayout;
  proteinPartAnnotations: PolymerLayout;
  proteinPIGraph: BasicProteinLayout;
  summaryIdentityGraph: {vMargin: number; slidingWindowSize: number; visible: boolean; height: number};
}

export function getDefaultProteinLayout(): ProteinLayout {
  return {
    aminoAcids: getDefaultAminoAcidsLayout(),
    antibodyNumbering: {
      type: {
        fontSize: 11,
      },
      vMargin: 10,
    },
    proteinAlignmentIdentityGraph: {
      height: 30,
      vMargin: 5,
    },
    proteinHydrophobicityGraph: {
      height: 30,
      vMargin: 5,
    },
    proteinLiabilitySites: {
      height: 12,
      vMargin: 6,
    },
    proteinPartAnnotations: {
      vMargin: 10,
      vMarginSingle: 2,
    },
    proteinParts: {
      height: 12,
      vMargin: 7,
    },
    proteinPIGraph: {
      height: 30,
      vMargin: 5,
    },
    // @ts-expect-error: note this is used by SummarySliceView but not SliceView
    summaryIdentityGraph: {
      slidingWindowSize: 5,
      visible: true,
      height: 30,
      vMargin: 5,
    },
  } satisfies {
    [key in ProteinLayouts]: PolymerLayout;
  };
}

export const TRANSCRIPTION_LINE_DEFAULT_HEIGHT = 4;
export const TRANSCRIPTION_LINE_HEIGHT_SCALE = 0.2;

export const BIOINTELLIGENT_LINKS_HIGHLIGHT_LAYOUT = {
  height: 12,
  vMargin: 0,
} satisfies PolymerLayout;

export const BIOINTELLIGENT_LINKS_HIGHLIGHT_SPACE_LAYOUT = {
  height: 0,
  vMargin: 0,
} satisfies PolymerLayout;

const PART_ANNOTATIONS_LAYOUT = {
  vMargin: 10,
  vMarginSingle: 2,
} satisfies PolymerLayout;

const PRIMERS_LAYOUT = {
  vMargin: 10,
  vMarginSingle: 2,
} satisfies PolymerLayout;

const TRANSLATIONS_LAYOUT = {
  vMargin: 5,
  vMarginSingle: 5,
} satisfies PolymerLayout;

export const DEFAULT_ALIGNMENT_LAYOUT: {
  primers: {vMargin: number; vMarginSingle: number};
  votes: {vMargin: number; svgHeight: number};
  nucleotidesSearch: {vMargin: number};
  translations: {vMargin: number; vMarginSingle: number};
} = {
  primers: PRIMERS_LAYOUT,
  votes: {
    vMargin: 0,
    svgHeight: 20,
  },
  nucleotidesSearch: {vMargin: 0},
  translations: TRANSLATIONS_LAYOUT,
} satisfies {
  [key in AlignmentLayouts]: PolymerLayout;
};

export const DEFAULT_DNA_SEQUENCE_LAYOUT: {
  forwardTranscriptionLine: {vMargin: number; visible: boolean; svgHeight: number};
  reverseTranscriptionLine: {vMargin: number; visible: boolean; svgHeight: number};
  transcriptionArrow: {vMargin: number; visible: boolean};
  sequenceBiointelligentLinkHighlights: {height: number; vMargin: number};
  reverseSequenceBiointelligentLinkHighlightsSpace: {height: number; vMargin: number};
  partAnnotations: {vMargin: number; vMarginSingle: number};
  transcriptionAnnotations: {vMargin: number; vMarginSingle: number};
  primers: {vMargin: number; vMarginSingle: number};
  nucleotidesSearch: {vMargin: number};
  codonOptimization: {vMargin: number};
  translations: {vMargin: number; vMarginSingle: number};
} = {
  forwardTranscriptionLine: {
    vMargin: 0,
    visible: true,
    svgHeight: TRANSCRIPTION_LINE_DEFAULT_HEIGHT,
  },
  reverseTranscriptionLine: {
    vMargin: 0,
    visible: true,
    svgHeight: TRANSCRIPTION_LINE_DEFAULT_HEIGHT,
  },
  transcriptionArrow: {
    vMargin: 0,
    visible: true,
  },
  sequenceBiointelligentLinkHighlights: BIOINTELLIGENT_LINKS_HIGHLIGHT_LAYOUT,
  reverseSequenceBiointelligentLinkHighlightsSpace: BIOINTELLIGENT_LINKS_HIGHLIGHT_SPACE_LAYOUT,
  partAnnotations: PART_ANNOTATIONS_LAYOUT,
  transcriptionAnnotations: PART_ANNOTATIONS_LAYOUT,
  primers: PRIMERS_LAYOUT,
  nucleotidesSearch: {vMargin: 0},
  codonOptimization: {vMargin: 0},
  translations: TRANSLATIONS_LAYOUT,
} satisfies {
  [key in SequenceLayouts]: PolymerLayout;
};

export const DEFAULT_RNA_SEQUENCE_LAYOUT: {
  modifiedNucleotides: {vMargin: number; visible: boolean};
  modifiedNucleotideSummary: {vMargin: number};
  nucleotidesAxisOverlay: {vMargin: number};
  nucleotidesPositionHighlight: {vMargin: number};
  sequenceBiointelligentLinkHighlights: {height: number; vMargin: number};
  reverseSequenceBiointelligentLinkHighlightsSpace: {height: number; vMargin: number};
  partAnnotations: {vMargin: number; vMarginSingle: number};
  primers: {vMargin: number; vMarginSingle: number};
  nucleotidesSearch: {vMargin: number};
  translations: {vMargin: number; vMarginSingle: number};
} = {
  modifiedNucleotides: {
    vMargin: 0,
    visible: false,
  },
  modifiedNucleotideSummary: {
    vMargin: 0,
  },
  nucleotidesAxisOverlay: {
    vMargin: AXIS_VMARGIN,
  },
  nucleotidesPositionHighlight: {
    vMargin: 0,
  },
  nucleotidesSearch: {vMargin: 0},
  sequenceBiointelligentLinkHighlights: BIOINTELLIGENT_LINKS_HIGHLIGHT_LAYOUT,
  reverseSequenceBiointelligentLinkHighlightsSpace: BIOINTELLIGENT_LINKS_HIGHLIGHT_SPACE_LAYOUT,
  partAnnotations: PART_ANNOTATIONS_LAYOUT,
  primers: PRIMERS_LAYOUT,
  translations: TRANSLATIONS_LAYOUT,
} satisfies {
  [key in ModifiedSequenceLayouts]: PolymerLayout;
};

export const DEFAULT_OLIGO_LAYOUT: {
  modifiedNucleotides: {vMargin: number; visible: boolean};
  nucleotidesAxisOverlay: {vMargin: number};
  nucleotidesPositionHighlight: {vMargin: number};
  nucleotidesSearch: {vMargin: number};
} = {
  modifiedNucleotides: {
    vMargin: 0,
    visible: true,
  },
  nucleotidesAxisOverlay: {
    vMargin: AXIS_VMARGIN,
  },
  nucleotidesPositionHighlight: {
    vMargin: 0,
  },
  nucleotidesSearch: {vMargin: 0},
} satisfies {
  [key in ModifiedOligoLayouts]: PolymerLayout;
};

export type AllPolymerLayouts = ProteinLayout &
  typeof DEFAULT_ALIGNMENT_LAYOUT &
  typeof DEFAULT_DNA_SEQUENCE_LAYOUT &
  typeof DEFAULT_RNA_SEQUENCE_LAYOUT &
  typeof DEFAULT_OLIGO_LAYOUT;

// Nucleotide sequence map residue constants
export const NUCLEOTIDE_MIN_FONT_SIZE = 3;
export const NUCLEOTIDE_MIN_VISIBLE_FONT_SIZE = 10;
export const NUCLEOTIDE_DEFAULT_FONT_SIZE = 15;
export const NUCLEOTIDE_MAX_FONT_SIZE = 20;
// Amino acids are larger than nucleotide residues by default
export const AMINO_ACID_MIN_VISIBLE_FONT_SIZE = 12;
export const AMINO_ACID_DEFAULT_FONT_SIZE = 18;
export const AMINO_ACID_MAX_FONT_SIZE = 24;
// A static zoom level where mods are visible.
export const DETAILED_NUCLEOTIDE_SEQUENCE_STATIC_ZOOM = 400;
export const NUCLEOTIDE_SEQUENCE_MIN_ZOOM = 20;
export const SEQUENCE_DEFAULT_ZOOM = 100;
export const LINEAR_MAX_ZOOM_NO_MODS = 300;

// Calculated so that proteins will always be visible
export const PROTEIN_MIN_ZOOM =
  (Type.widths[AMINO_ACID_MIN_VISIBLE_FONT_SIZE] * SEQUENCE_DEFAULT_ZOOM) /
  Type.widths[AMINO_ACID_DEFAULT_FONT_SIZE];

export const LINEAR_LAYER_FULL = 'fullLayer';
export const ALIGNMENT_TYPE = ProteinAlignmentKind;
export type LayeringMode =
  | typeof LINEAR_LAYER_FULL
  | typeof LINEAR_LAYER_INLINE
  | typeof LINEAR_LAYER_INLINE_PDF;

export const SEQUENCE_MAP_TYPE_USER_PREFERENCE_KEY = 'MOLBIO_SEQUENCE_MAP_TYPE';

// Enum indicating a top-level Map UI for a single Sequence, not to be confused with the "Type" class
// used in maps, nor the SliceMapType enum which excludes the Plasmid map since it focused on slices
// and includes alignment maps.
export enum MapType {
  PLASMID = 'plasmid',
  LINEAR_SEQUENCE = 'linear',
  MINIMAP = 'minimap',
}

export enum SliceMapType {
  minimap = 'minimap',
  linear = 'linear',
  alignment = 'alignment',
  proteinAlignment = 'proteinAlignment',
  // Is "summary" a valid value?
}

export const MAX_VISIBLE_RING_FEATURES = 100;
